import Spacer from '$tablet-app/src/app/components/Spacer';
import { Button } from '@akin-home/buttons/native';
import { Text } from '@akin-home/text/native';
import { colors, spacing } from '@akin-home/theme';
import { useBool } from '@akin-home/utils/react';
import { ScrollView, View, StyleSheet } from 'react-native';
import { Overlay } from 'react-native-elements';

type EmotionHelpType = {
  label: string;
  icon: string;
  description: string;
};

const EMOTION_HELP_DATA: EmotionHelpType[] = [
  {
    label: 'Interest',
    icon: '🤓',
    description:
      'Interest is a feeling of curiosity that draws the attention of a person towards a subject, activity or idea.',
  },
  {
    label: 'Amusement',
    icon: '😄',
    description:
      'Amusement is an emotion characterised by enjoyment and can often evoke laughter or smiles.',
  },
  {
    label: 'Pride',
    icon: '☺️',
    description:
      'Pride is a feeling of self-satisfaction or achievement, it can arise from the accomplishment or success of oneself or others.',
  },
  {
    label: 'Joy',
    icon: '😄',
    description:
      'Joy is an emotion characterised by feelings of happiness and delight.',
  },
  {
    label: 'Pleasure',
    icon: '😌',
    description:
      'Pleasure is a feeling of enjoyment and satisfaction, it can arise from engaging in positive experiences.',
  },
  {
    label: 'Contentment',
    icon: '😊',
    description:
      'Contentment refers to a calm or peaceful feeling that one’s needs are being met.',
  },
  {
    label: 'Love',
    icon: '🥰',
    description: 'Love is a feeling of strong affection and care for others.',
  },
  {
    label: 'Admiration',
    icon: '🥹',
    description:
      'Admiration is a feeling of respect and appreciation for others, particularly their skills or achievements.',
  },
  {
    label: 'Relief',
    icon: '😮‍💨',
    description:
      'Relief is an emotion that occurs when a source of anxiety or stress is removed.',
  },
  {
    label: 'Compassion',
    icon: '🤗',
    description:
      'Compassion is characterised by empathy and concern for others.',
  },
  {
    label: 'Sadness',
    icon: '☹️',
    description:
      'Sadness combines a feeling of loss, loneliness or helplessness, and can typically feel like a sense of heaviness or emptiness.',
  },
  {
    label: 'Guilt',
    icon: '😓',
    description:
      'Guilt is a feeling when a person believes they have done something wrong, and often involves a feeling of worry or regret.',
  },
  {
    label: 'Regret',
    icon: '😟',
    description:
      'Regret is a feeling of that occurs when someone thinks that a better outcome could have been achieved if they had made a different choice.',
  },
  {
    label: 'Shame',
    icon: '😔',
    description:
      'Shame is a feeling when one believes that they will be perceived negatively by others, leading to feelings of embarrassment or rejection.',
  },
  {
    label: 'Disappointment',
    icon: '😞',
    description:
      'Disappointment can arise when expectations or hopes are not met, leading to sadness or frustration.',
  },
  {
    label: 'Fear',
    icon: '😨',
    description:
      'Fear is a strong unpleasant feeling in response to an event or situation.',
  },
  {
    label: 'Disgust',
    icon: '😖',
    description:
      'Disgust is an emotion consisting of an aversion towards something offensive or unpleasant.',
  },
  {
    label: 'Contempt',
    icon: '😠',
    description:
      'Contempt is an emotion that involves feeling superior to someone or something and viewing them as beneath consideration.',
  },
  {
    label: 'Hate',
    icon: '😡',
    description:
      'Hate is an intense emotion characterised by extreme dislike of people, things or ideas.',
  },
  {
    label: 'Anger',
    icon: '🤬',
    description:
      'Anger is a feeling of hostility toward someone or something you believe has intentionally wronged you.',
  },
];

export function EmotionPickerHelpButton() {
  const [isModalVisible, showModal, hideModal] = useBool(false);

  return (
    <>
      <Button variant="secondary" icon={'question-circle'} onPress={showModal}>
        Help
      </Button>

      <Overlay
        onDismiss={hideModal}
        onBackdropPress={hideModal}
        isVisible={isModalVisible}
        overlayStyle={styles.overlayStyles}
      >
        <View style={styles.titleContainer}>
          <Text style={styles.titleText} variant="h2" weight="semibold">
            Explanation of Emotions
          </Text>
          <Button
            style={styles.closeButton}
            onPress={hideModal}
            icon="times"
            variant="secondary"
          ></Button>
        </View>

        <ScrollView contentContainerStyle={styles.scrollContainer}>
          {EMOTION_HELP_DATA.map((row, index) => (
            <View key={`${row.label}_${index}`} style={styles.emotionContainer}>
              <Text
                style={styles.emotionLabelText}
                variant="p"
                bold
                adjustsFontSizeToFit
                numberOfLines={1}
              >
                {row.icon} {row.label}
              </Text>

              <Spacer height={'100%'} width={'1px'} color={'lightgrey'} />

              <Text style={styles.emotionDescriptionText}>
                {row.description}
              </Text>
            </View>
          ))}
        </ScrollView>
      </Overlay>
    </>
  );
}

const styles = StyleSheet.create({
  overlayStyles: {
    backgroundColor: 'white',
    shadowColor: 'transparent',
    width: '60%',
    minWidth: 700,
    margin: spacing[6],
    borderRadius: 10,
    maxHeight: '95%',
  },
  titleContainer: {
    margin: spacing[2],
  },
  titleText: {
    alignSelf: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollContainer: {
    gap: spacing[4],
    paddingVertical: spacing[2],
  },
  emotionContainer: {
    backgroundColor: colors.grey[200],
    elevation: 2,
    marginHorizontal: spacing[2],
    padding: spacing[2],
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    minHeight: 80,
  },
  emotionLabelText: {
    width: '20%',
    paddingLeft: spacing[1],
  },
  emotionDescriptionText: {
    flex: 1,
    textAlignVertical: 'center',
  },
});
